import { PageLayout } from '@kcftech/design-system';
import { LocationsMultiSelect } from 'components/reports/LocationsMultiSelect';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { ReactNode } from 'react';
import { BarChart, PieChart, ResponsiveContainer } from 'recharts';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

const cardContainerStyles = css`
    border-radius: 12px;
    background-color: ${tokens.background.default};
    outline: 1px solid ${tokens.border.default};
    padding: 1.6rem;
`;

export const Card = styled.div`
    ${cardContainerStyles}
    display: grid;
    gap: 1rem;
    grid-template-rows: min-content 1fr;
    & > :not(:first-child) {
        justify-self: stretch;
        align-self: stretch;
    }
`;

export const AccentCard = styled.div`
    ${cardContainerStyles}
    display: grid;
    gap: 1rem;
    grid-template-rows: min-content 1fr;
    & > :not(:first-child) {
        justify-self: stretch;
        align-self: stretch;
    }
    padding: 0;
    outline-width: 2px;
    background: none;
`;

export const CardTitle = styled.h2`
    color: ${tokens.text.primary};
    font-size: 1.7rem;
    font-weight: 500;
    line-height: 2rem;
    margin: 0;
`;

const CardHeaderWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding-bottom: 0.8rem;
    border-bottom: 1px solid ${tokens.border.default};
`;

export type CardHeaderProps = {
    title: string;
    description: ReactNode;
};

export function CardHeader({ title, description }: CardHeaderProps): JSX.Element {
    return (
        <CardHeaderWrapper>
            <CardTitle>{title}</CardTitle>
            <InfoPopup title={`read more about '${title}'`}>{description}</InfoPopup>
        </CardHeaderWrapper>
    );
}

export const CardContent = styled.div<{
    $contentPositioning: 'center' | 'stretch' | undefined;
}>`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    ${({ $contentPositioning }) =>
        $contentPositioning !== undefined
            ? `
align-items: ${$contentPositioning};
justify-content: ${$contentPositioning};
    `
            : ''}
    overflow: hidden;
    position: relative;
`;

/**
 * Dashboard header with logo and filters
 */
export const Header = styled.header`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    padding: 1.8rem;
    border-radius: 8px;
    background-color: ${tokens.background.default};

    @media ${MediaQueries.largeAndDown} {
        flex-direction: column;
        align-items: flex-start;
        gap: 2rem;
    }
`;

export const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1rem;

    @media ${MediaQueries.largeAndDown} {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        gap: 1.5rem;
    }
`;

export const QuickFilterHeaderRegion = styled.div`
    border: 1px solid ${tokens.border.default};
    border-radius: 8px;
    padding: 0.5rem;
    display: flex;
    gap: 1rem;
    & > * {
        background-color: ${tokens.background.default};
        border-color: ${tokens.border.default};
        border-radius: 8px;
        &:active {
            outline: 1px solid ${tokens.primary.default};
        }
    }
`;

export const StyledLocationsMultiSelect = styled(LocationsMultiSelect)`
    width: 28rem;
    padding-bottom: 0;

    @media ${MediaQueries.largeAndDown} {
        width: 100%;
    }
`;

export const StyledRechartsResponsiveContainer = styled(ResponsiveContainer)`
    text {
        fill: ${tokens.text.primary};
    }

    .recharts-cartesian-grid line {
        stroke: ${tokens.nav.border};
    }

    line.recharts-cartesian-axis-line
    /* .recharts-cartesian-axis-tick line  */ {
        stroke: ${tokens.text.secondary};
    }

    .recharts-tooltip-cursor {
        fill: ${tokens.background.subtle};
        fill-opacity: 90%;
    }

    .recharts-tooltip-wrapper {
        fill: blue;
    }

    .recharts-default-tooltip {
        background-color: ${tokens.background.default} !important;
        border: 1px solid ${tokens.nav.border} !important;
        border-radius: 5px;
    }
    .recharts-tooltip-label {
        fill: ${tokens.text.primary};
        color: ${tokens.text.primary};
    }

    // bar labels
    .recharts-layer .recharts-label-list text.recharts-text {
        font-size: 1.2rem;
    }

    text,
    .recharts-text.recharts-cartesian-axis-tick-value,
    .recharts-text.recharts-cartesian-axis-tick-value tspan {
        color: ${tokens.text.secondary};
        fill: ${tokens.text.secondary};
        font-size: 1.4rem;
    }
`;

export const StyledPieChart = styled(PieChart)`
    text {
        fill: ${tokens.text.primary};
    }

    path.recharts-sector {
        stroke: ${tokens.border.default};
    }
`;

export const DashboardPageLayout = styled(PageLayout.Primary)`
    padding-top: 1.6rem;
    padding-bottom: 1.6rem;
    box-sizing: border-box;
    background-color: transparent;
    display: grid;
    gap: 1.2rem;
    --dashboard-header-height: 6.4rem;

    .head {
        ${cardContainerStyles}
        grid-area: head;
        align-content: center;
        & h1 {
            font-size: 2.4rem;
            line-height: 1.25;
        }
    }
`;

/**
 * Shared chart styled components
 */

export const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    min-height: 20rem;
`;

export const StyledBarChart = styled(BarChart)`
    text {
        fill: ${tokens.text.primary};
    }
`;

export const CenteredChartContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const PieChartLegendContainer = styled.div<{
    $gap?: string;
    $justifyContent?: string;
}>`
    display: flex;
    flex-direction: column;
    gap: ${({ $gap }) => $gap || '1rem'};
    padding: 1rem;
    ${({ $justifyContent }) => $justifyContent && `justify-content: ${$justifyContent};`}
`;

export const PieChartLegendItem = styled.div<{
    $color: string;
    $variant?: 'circle' | 'square';
    $gap?: string;
}>`
    display: flex;
    align-items: center;
    gap: ${({ $gap }) => $gap || '0.8ch'};
    color: ${tokens.text.secondary};
    font-size: 1.4rem;

    &::before {
        content: '';
        display: block;
        width: 1.6rem;
        height: 1.6rem;
        border-radius: ${({ $variant }) => ($variant === 'square' ? '4px' : '50%')};
        background-color: ${({ $color }) => $color};
    }
`;

export const SegmentedControl = styled.div`
    display: inline-flex;
    gap: 0;
    border: 1px solid ${tokens.border.default};
    border-radius: 8px;
    padding: 0.4rem;
    background-color: ${tokens.background.default};
`;

export const SegmentButton = styled.button<{ $isActive: boolean }>`
    padding: 0.4rem 1.2rem;
    border: none;
    border-radius: 6px;
    background-color: ${({ $isActive }) =>
        $isActive ? tokens.primary.default : 'transparent'};
    color: ${({ $isActive }) =>
        $isActive ? tokens.background.default : tokens.text.secondary};
    font-size: 1.3rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background-color: ${({ $isActive }) =>
            $isActive ? tokens.primary.default : tokens.background.subtle};
        color: ${({ $isActive }) =>
            $isActive ? tokens.background.default : tokens.text.primary};
    }

    &:focus-visible {
        outline: 2px solid ${tokens.primary.default};
        outline-offset: 2px;
    }
`;

export const ViewToggleContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
`;
